import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { trackingAllowed } from "@/lib/cookieConsent";

/**
 * Privacy-respecting first-party analytics tracker.
 *
 * Fires once per route change. Mounted at the app root so it runs on every
 * page including unauthenticated marketing routes. Admin pages are excluded
 * to keep analytics free of internal-staff noise.
 *
 * Uses `navigator.sendBeacon` when available so the request survives even if
 * the user navigates away mid-request. Falls back to `fetch(keepalive: true)`.
 */
export default function AnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const path = location.pathname;
    // Skip admin pages — we don't want internal staff polluting visitor stats.
    if (path.startsWith("/app/admin")) return;
    // Respect the visitor's cookie-consent choice. If they denied tracking
    // for this session, do nothing.
    if (!trackingAllowed()) return;

    const url = `${API}/analytics/track`;
    const body = JSON.stringify({
      path,
      referrer: typeof document !== "undefined" ? document.referrer : "",
      user_id: user?.id || null,
    });

    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
    } catch { /* fall through to fetch */ }

    // sendBeacon unavailable → best-effort keepalive fetch
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  }, [location.pathname, user?.id]);

  return null;
}
